import axios, { AxiosRequestConfig } from 'axios';
import { debounce } from 'lodash';

import { implog } from '@/api/implog';
import { ACCESS_TOKEN_KEY, ACCESS_USERID_KEY } from '@/constant/config';
import { history } from '@/routers/history';
import globalStore from '@/stores/store';

import { getCookie } from './cookie';
import { Storage } from './Storage';
import { toast } from './toast';
import { getChannelFlag } from './utils';

// import { history } from '@/components/history';

export interface RequestOptions {
  /** 是否直接获取data，而忽略message等 */
  isGetDataDirectly?: boolean;
  /** 是否跳过错误提示 */
  skipErrorHandle?: boolean;
  /** 请求成功是提示信息 */
  successMsg?: string;
  /** 请求失败是提示信息 */
  errorMsg?: string;
  /** 是否保持 data 原结构 */
  keep?: boolean;
}

const UNKNOWN_ERROR = 'Network error, please try again.';
const PRODUCT_UNKNOWN_ERROR = 'Network error, please try again later.';

const service = axios.create({
  // baseURL: '/api',
  timeout: 30000
});

const debounceToast = debounce(
  msg => {
    if (!window.location.pathname.includes('/login')) {
      const source = getAPKCHSource().toLocaleLowerCase();
      const flag = getChannelFlag();
      const basename = flag && source ? `/${source}/${flag}` : '';
      const pathname = window.location.pathname.replace(basename, '');
      history?.push(`/login?origin=${encodeURIComponent(pathname + window.location.search)}`, {
        replace: true
      });
      // window.location.href = `login?origin=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    }
    globalStore.onGlobalStateChange({
      isLogin: false
    });
    toast(msg, { id: 'request-error-not-login' });
  },
  1500,
  {
    leading: true,
    trailing: false
  }
);

service.interceptors.request.use(
  config => {
    const token = Storage.getAccess(ACCESS_TOKEN_KEY);
    const userid = Storage.getAccess(ACCESS_USERID_KEY);
    if (token && config.headers) {
      // 请求头token信息，请根据实际情况进行修改
      config.headers['token'] = token;
      config.headers['userid'] = userid;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  response => {
    const res = response.data;
    if ((response.config as any).skipErrorHandle) {
      return res;
    }
    if (res instanceof ArrayBuffer || res instanceof Blob) {
      return response;
    }
    if (res.ret !== 0) {
      // message.error(res.msg || PRODUCT_UNKNOWN_ERROR);
      if (res.ret === -1 && res?.isNeedRelogin && response?.config?.headers?.['token']) {
        implog(
          'exception_logout',
          response?.config?.url ? encodeURIComponent(response?.config?.url) : '',
          response?.config?.headers?.['userid']
        );

        debounceToast(res?.msg || res?.err || 'You need to log in.');
      } else {
        toast(res?.msg || res?.err || PRODUCT_UNKNOWN_ERROR, { id: 'error-response' });
      }

      // throw other
      const error = new Error(res?.msg || res?.err || PRODUCT_UNKNOWN_ERROR) as Error & {
        code: any;
      };
      error.code = res.code;
      return Promise.reject(error);
    } else {
      return res;
    }
  },
  error => {
    // 处理 422 或者 500 的错误异常提示
    if (error.name === 'CanceledError') {
      return Promise.reject(error);
    }
    const errMsg = error?.response?.data?.message ?? UNKNOWN_ERROR;
    toast(errMsg, { id: 'error-response-422-500' });
    error.message = errMsg;
    return Promise.reject(error);
  }
);

export type Response<T = any> = {
  code: number;
  message: string;
  data: T;
};

export type BaseResponse<T = any> = Promise<Response<T>>;

/**
 *
 * @param method - request methods
 * @param url - request url
 * @param data - request data or params
 */
export const request = async <T = any>(config: AxiosRequestConfig, options: RequestOptions = {}): Promise<T> => {
  try {
    const { skipErrorHandle } = options;
    const { data, method = 'get', params } = config;
    const BASE_URL = getAPKCHSource().replace('CH_', '') || 'H5US4MAIN_001';
    const p_userid = Storage.getAccess(ACCESS_USERID_KEY);
    const machineserial = await getUniqueID();
    if (method === 'get') {
      config.params = {
        ...params,
        p_source: BASE_URL,
        p_userid,
        machineserial
      };
    } else if (!options.keep) {
      config.data = {
        ...data,
        p_source: BASE_URL,
        machineserial
      };
    }

    const fullUrl = `${config.url}`;
    config.url = (fullUrl as string).replace(/([^:])\/+/g, '$1/');
    const ORIGINAL_DOMAIN = getCookie('ORIGINAL_DOMAIN');
    if (fullUrl.includes('v1/upload') || fullUrl.includes('http')) {
      config.url = fullUrl;
    } else {
      config.url = `${ORIGINAL_DOMAIN ? `${ORIGINAL_DOMAIN}/api` : '/api'}${fullUrl}`;
    }
    (config as any).skipErrorHandle = skipErrorHandle;

    return await service.request(config);
  } catch (error: any) {
    return Promise.reject(error);
  }
};
